import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Search, Gavel, MessageSquare, Building2, Hotel, ArrowRight, Bot, Wallet, Gift, Sparkles, TrendingDown, Calendar } from "lucide-react";

const QUICK_ACTIONS = [
  {
    to: "/search",
    title: "Find Hotels",
    desc: "Search verified hotels · Real-time bids from partners in seconds.",
    icon: Search,
    gradient: "from-[#0F1115] via-[#1A1D24] to-[#2A2E38]",
    accent: "text-[#E8D8A8]",
  },
  {
    to: "/requests",
    title: "Group Booking Bid",
    desc: "Tell hotels your city, dates & budget. AI-vetted bids arrive within minutes.",
    icon: Building2,
    gradient: "from-[#A6852F] via-[#C8A34D] to-[#E8D8A8]",
    accent: "text-white/90",
  },
  {
    to: "/negotiations",
    title: "Active Negotiations",
    desc: "Track your offers, counters and Mythri AI recommendations.",
    icon: Gavel,
    gradient: "from-[#16A34A] via-[#15803D] to-[#0F5934]",
    accent: "text-emerald-100",
  },
];

const MICRO_STATS = [
  { key: "wallet", label: "Wallet", icon: Wallet, color: "bg-white text-[#A6852F] border-[#C8A34D]/40", to: "/wallet" },
  { key: "rewards", label: "Rewards", icon: Gift, color: "bg-white text-[#A6852F] border-[#C8A34D]/40", to: "/rewards" },
  { key: "bookings", label: "Bookings", icon: Calendar, color: "bg-white text-[#0F1115] border-slate-200", to: "/bookings" },
  { key: "mythri", label: "Ask Mythri", icon: Bot, color: "bg-white text-[#0F5934] border-[#16A34A]/40", to: null },
];

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [negs, setNegs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [wallet, setWallet] = useState(0);
  const [rewardPoints, setRewardPoints] = useState(0);

  useEffect(() => {
    api.get("/negotiations/mine").then(({ data }) => setNegs(data)).catch((e) => console.debug("fetch failed:", e?.message));
    api.get("/booking-requests", { params: { mine: true } }).then(({ data }) => setReqs(data)).catch((e) => console.debug("fetch failed:", e?.message));
    api.get("/bookings/mine").then(({ data }) => setBookings(data)).catch((e) => console.debug("fetch failed:", e?.message));
    api.get("/wallet").then(({ data }) => setWallet(data.balance || 0)).catch((e) => console.debug("fetch failed:", e?.message));
    api.get("/rewards").then(({ data }) => setRewardPoints(data.points || 0)).catch((e) => console.debug("fetch failed:", e?.message));
  }, []);

  const stats = {
    wallet: `₹${wallet.toLocaleString("en-IN")}`,
    rewards: `${rewardPoints.toLocaleString("en-IN")} pts`,
    bookings: `${bookings.length}`,
    mythri: "AI",
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      {/* Premium dark hero */}
      <div className="relative overflow-hidden ss-surface ss-hero-gradient ss-noise">
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="ss-label text-[#E8D8A8] flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Welcome back
          </div>
          <h1 className="font-head text-4xl md:text-5xl font-black text-white mt-3" data-testid="customer-heading">
            Namaste, <span className="ss-text-gold-soft">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-white/70 mt-2 max-w-xl text-sm md:text-base font-body">
            Ready for your next stay? Post a need, browse verified hotels, or let Mythri AI find the perfect deal for you.
          </p>

          {/* Micro stats pills */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="micro-stats">
            {MICRO_STATS.map((s) => {
              const Item = s.to ? Link : "div";
              const props = s.to ? { to: s.to } : {};
              return (
                <Item
                  key={s.key}
                  {...props}
                  className={`p-4 rounded-xl bg-white/95 backdrop-blur border ${s.color} transition-all hover:-translate-y-0.5 hover:shadow-lg cursor-pointer`}
                  data-testid={`micro-stat-${s.key}`}
                >
                  <div className="flex items-center gap-2">
                    <s.icon className="w-4 h-4" />
                    <div className="ss-label">{s.label}</div>
                  </div>
                  <div className="font-head font-black text-2xl mt-1 text-slate-900" data-testid={`stat-value-${s.key}`}>{stats[s.key]}</div>
                </Item>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Quick action cards — colorful gradient */}
        <h2 className="font-head text-2xl md:text-3xl font-black text-slate-900">Quick actions</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-6">
          {QUICK_ACTIONS.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`quick-${c.to.replace(/\//g, "")}`}
              className={`relative overflow-hidden p-6 rounded-2xl text-white bg-gradient-to-br ${c.gradient} shadow-md hover:shadow-2xl transition-all hover:-translate-y-1 group`}
            >
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 group-hover:scale-110 transition-transform" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-white/5" />
              <div className="relative">
                <c.icon className="w-8 h-8 opacity-90" strokeWidth={2.2} />
                <h3 className="font-head text-xl font-black mt-6">{c.title}</h3>
                <p className={`text-sm mt-1 font-body ${c.accent}`}>{c.desc}</p>
                <div className="mt-5 ss-label inline-flex items-center gap-1 opacity-90 group-hover:translate-x-1 transition-transform">
                  Open <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Data sections — colorful accents */}
        <div className="grid lg:grid-cols-3 gap-5 mt-10">
          <DashSection
            title="Recent negotiations"
            link="/negotiations"
            data={negs}
            accent="from-[#A6852F] via-[#C8A34D] to-[#E8D8A8]"
            icon={Gavel}
            renderItem={(n) => (
              <div className="text-sm">
                <div className="font-semibold text-slate-900 truncate">{n.hotel_name}</div>
                <div className="text-slate-500 flex items-center gap-2 mt-1 font-body">
                  <TrendingDown className="w-3 h-3 text-[#A6852F]" />
                  Offer ₹{n.offer_price} · <span className="ss-label text-[#A6852F]">{n.status}</span>
                </div>
              </div>
            )}
            testid="dash-negotiations"
          />
          <DashSection
            title="Booking requests"
            link="/requests"
            data={reqs}
            accent="from-[#0F1115] via-[#1A1D24] to-[#2A2E38]"
            icon={Building2}
            renderItem={(r) => (
              <div className="text-sm">
                <div className="font-semibold text-slate-900 truncate">{r.city} · {r.rooms_needed} rooms</div>
                <div className="text-slate-500 mt-1 font-body">{r.event_type} · <span className="ss-label text-[#A6852F]">{r.status}</span></div>
              </div>
            )}
            testid="dash-requests"
          />
          <DashSection
            title="Recent bookings"
            link="/bookings"
            data={bookings}
            accent="from-[#16A34A] via-[#15803D] to-[#0F5934]"
            icon={Hotel}
            renderItem={(b) => (
              <div className="text-sm">
                <div className="font-semibold text-slate-900 truncate">{b.hotel_name}</div>
                <div className="text-slate-500 mt-1 font-body">₹{b.price} · <span className="ss-label text-[#16A34A]">{b.status}</span></div>
              </div>
            )}
            testid="dash-bookings"
          />
        </div>
      </div>
    </div>
  );
}

function DashSection({ title, link, data, renderItem, testid, accent, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-[#C8A34D] hover:shadow-md transition-all" data-testid={testid}>
      <div className={`bg-gradient-to-r ${accent} px-5 py-3 text-white flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <div className="ss-label text-white/90">{title}</div>
        </div>
        <Link to={link} className="ss-label text-white/90 hover:text-white flex items-center gap-1">
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="p-5">
        {data.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing here yet.</p>
        ) : (
          <div className="space-y-3">
            {data.slice(0, 4).map((it, i) => (
              <div key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">{renderItem(it)}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
